import { Children, cloneElement, isValidElement, type FormHTMLAttributes, type ReactElement, type ReactNode } from 'react';

/**
 * 표 형태 등록·편집 폼 (스크린샷 규약).
 *  - FormSheet: 상단 설명, 섹션들, 하단 우측 액션(취소/저장) — CR-022
 *  - FormSection: 굵은 소제목 + 2열(라벨|입력|라벨|입력) 격자
 *  - FormField: 라벨 셀(회색 배경, 필수 *) + 입력 셀(입력 + 회색 힌트). span=2 면 한 줄 전체
 */
export function FormSheet({ description, actions, children, ...rest }: FormHTMLAttributes<HTMLFormElement> & { description?: ReactNode; actions?: ReactNode }) {
  return (
    <form {...rest} className={`ft-sheet ${rest.className ?? ''}`}>
      {description && <div className="ft-head"><div className="ft-desc">{description} <span className="ft-req">*</span> 는 필수 항목입니다.</div></div>}
      {children}
      {actions && <div className="ft-foot">{actions}</div>}
    </form>
  );
}
export function FormSection({ title, children, cols = 2 }: { title?: ReactNode; children: ReactNode; cols?: 1 | 2 | 3 }) {
  // 마지막 행의 셀에는 아래 테두리를 그리지 않는다 (격자 외곽선과 겹쳐 2px 로 보이는 것 방지)
  const items = Children.toArray(children).filter(isValidElement) as ReactElement<{ span?: number; _last?: boolean }>[];
  const rows: number[] = []; let col = 0, row = 0;
  for (const it of items) { const span = Math.min(cols, it.props.span ?? 1); if (col + span > cols) { row++; col = 0; } rows.push(row); col += span; if (col >= cols) { row++; col = 0; } }
  const lastRow = rows.length ? (col === 0 ? row - 1 : row) : 0;
  return (
    <section className="ft-section">
      {title && <h3 className="ft-title">{title}</h3>}
      <div className={`ft-grid ft-cols-${cols}`}>{items.map((it, i) => cloneElement(it, { _last: rows[i] === lastRow }))}</div>
    </section>
  );
}
export function FormField({ label, required, hint, span, children, htmlFor, _last }: { label: ReactNode; required?: boolean; hint?: ReactNode; span?: 1 | 2 | 3; children: ReactNode; htmlFor?: string; _last?: boolean }) {
  return (
    <div className={`ft-cell ${span ? `ft-span-${span}` : ''} ${_last ? 'ft-last' : ''}`}>
      <label className="ft-label" htmlFor={htmlFor}>{label}{required && <span className="ft-req"> *</span>}</label>
      <div className="ft-input">{children}{hint && <span className="ft-hint">{hint}</span>}</div>
    </div>
  );
}
/** 단위 붙은 숫자 입력 (예: 10 분) */
export function UnitInput({ unit, className = '', ...props }: React.InputHTMLAttributes<HTMLInputElement> & { unit: string }) {
  return <span className="ft-unit"><input {...props} className={`ft-control ft-num ${className}`} /><span className="ft-unit-label">{unit}</span></span>;
}
/** 표 폼 안에서 쓰는 입력 클래스 */
export const ftInput = 'ft-control';
/** 노란 안내 상자 (규칙·주의 사항 목록) */
export function FormNotice({ items }: { items: ReactNode[] }) {
  return <ul className="ft-notice">{items.map((it, i) => <li key={i}>{it}</li>)}</ul>;
}
